import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { createRoot } from 'react-dom/client';
import { createPortal } from 'react-dom';
import jsPDF from 'jspdf';
import { useTranslation } from 'react-i18next';
import { ReactComponent as DraftIcon } from '../../../svg/edit-3-svgrepo-com.svg';
import { ReactComponent as ShapesIcon } from '../../../svg/clipboard-check-svgrepo-com.svg';
import vcontrolAPI from '../../../services/documentApi';
import PrintStage from './PrintStage';
import { recalculateShapeMeasurements } from '../utils/drawingUtils';
import { getOrderedShapesFromPageData } from '../utils/shapePersistence';
import {
  getCachedThumbnail,
  cacheThumbnail,
  clearThumbnailCache,
  removeThumbnailFromCache,
} from '../utils/thumbnailCache';
import './PageSelectorModal.css';

// Re-export clearThumbnailCache so other files don't break
export { clearThumbnailCache };

/**
 * decodePreferenceSegment - helper for decoding Firebase preferences

 * decodePreferenceSegment - helper for decoding Firebase preferences
 */
const decodePreferenceSegment = (segment) => {
  try {
    return decodeURIComponent(segment);
  } catch (_error) {
    return segment;
  }
};

const PRINT_QUALITY_PRESETS = {
  draft: { jpegQuality: 0.3, pixelRatio: 1 },
  low: { jpegQuality: 0.6, pixelRatio: 1 },
  standard: { jpegQuality: 0.75, pixelRatio: 1.5 },
  high: { jpegQuality: 0.85, pixelRatio: 2 },
};

/**
 * PageThumbnail - Individual thumbnail component with lazy loading and caching
 * Only loads the thumbnail when it becomes visible in the viewport
 * Checks cache first to avoid redundant server requests
 * Uses AbortController to cancel pending requests on unmount/page change
 */
const PageThumbnail = ({
  page,
  isSelected,
  onSelect,
  isVisible,
  hasShapes,
  hasDraft,
  isSelectionMode,
  isChecked,
  onCheckChange,
}) => {
  const hasUnpublishedDraft = hasDraft && !hasShapes;
  const { t } = useTranslation();
  const [thumbnailUrl, setThumbnailUrl] = useState(() =>
    getCachedThumbnail(page.documentId, page.actualPageNumber),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const abortControllerRef = useRef(null);

  // Reset thumbnail state when page changes (e.g., after project change)
  useEffect(() => {
    // Cancel any pending request when page changes
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    const cached = getCachedThumbnail(
      page.documentId,
      page.actualPageNumber,
    );
    setThumbnailUrl(cached || null);
    setIsLoading(false);
    setError(false);
  }, [page.documentId, page.actualPageNumber]);

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const loadThumbnail = useCallback(async () => {
    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    setError(false);
    try {
      const blob = await vcontrolAPI.getThumbnail(
        page.documentId,
        page.actualPageNumber,
        { signal: abortControllerRef.current.signal },
      );

      // Check if request was aborted
      if (abortControllerRef.current?.signal.aborted) {
        return;
      }

      const url = URL.createObjectURL(blob);

      // Store in cache for future use
      cacheThumbnail(page.documentId, page.actualPageNumber, url);
      setThumbnailUrl(url);
    } catch (err) {
      // Ignore abort errors
      if (err.name === 'AbortError') {
        return;
      }
      console.error(
        `Failed to load thumbnail for page ${page.pageLabel}:`,
        err,
      );
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [page.documentId, page.actualPageNumber, page.pageLabel]);

  const handleRetry = useCallback(() => {
    // Clear cache entry for this thumbnail so it will be fetched again
    removeThumbnailFromCache(page.documentId, page.actualPageNumber);

    // Reset state and reload
    setThumbnailUrl(null);
    setError(false);
    loadThumbnail();
  }, [page.documentId, page.actualPageNumber, loadThumbnail]);

  // Load thumbnail only when visible and not already cached
  useEffect(() => {
    if (!isVisible || thumbnailUrl || isLoading) return;

    // Check cache again in case it was populated while waiting
    const cached = getCachedThumbnail(
      page.documentId,
      page.actualPageNumber,
    );
    if (cached) {
      setThumbnailUrl(cached);
      return;
    }

    loadThumbnail();
  }, [
    isVisible,
    page.documentId,
    page.actualPageNumber,
    thumbnailUrl,
    isLoading,
    loadThumbnail,
  ]);

  // Note: We don't revoke URLs on unmount since they're managed by the cache

  return (
    <div
      className={`page-thumbnail ${isSelected ? 'selected' : ''}`}
      onClick={() => onSelect(page)}
      title={page.pageLabel}
    >
      <div className="thumbnail-image-container">
        {(hasShapes || hasUnpublishedDraft) && (
          <div
            className={`shape-badge ${hasUnpublishedDraft ? 'shape-badge--draft' : 'shape-badge--shapes'}`}
            title={
              hasUnpublishedDraft
                ? t('pageSelector.has_drafts')
                : t('pageSelector.has_drawings')
            }
          >
            <span className="badge-icon">
              {hasUnpublishedDraft ? (
                <DraftIcon className="badge-svg" />
              ) : (
                <ShapesIcon className="badge-svg" />
              )}
            </span>
          </div>
        )}
        {/* Selection checkbox - only visible in selection mode */}
        {isSelectionMode && (
          <div
            className="thumbnail-checkbox"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={isChecked}
              onChange={(e) =>
                onCheckChange(page.pageIndex, e.target.checked)
              }
              title={t('pageSelector.select_for_printing')}
            />
          </div>
        )}
        {isLoading && (
          <div aria-busy="true" className="thumbnail-loading"></div>
        )}
        {error && !isLoading && (
          <div className="thumbnail-error">
            <span title={t('pageSelector.click_to_retry')}>⚠️</span>
            <button
              className="thumbnail-retry-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleRetry();
              }}
              title={t('pageSelector.retry_loading_thumbnail')}
            >
              {t('pageSelector.retry')}
            </button>
          </div>
        )}
        {thumbnailUrl && !isLoading && (
          <img
            src={thumbnailUrl}
            crossOrigin="anonymous"
            alt={page.pageLabel}
            className="thumbnail-image"
            loading="lazy"
          />
        )}
        {!thumbnailUrl && !isLoading && !error && (
          <div className="thumbnail-placeholder">
            <span>{page.pageIndex + 1}</span>
          </div>
        )}
      </div>
      <div className="thumbnail-label">{page.pageLabel}</div>
    </div>
  );
};

const PrintProgressModal = ({
  current,
  total,
  onCancel,
  statusMessage,
}) => {
  const { t } = useTranslation();
  return createPortal(
    <div className="page-selector-backdrop">
      <div className="print-progress-modal">
        <h3>{t('pageSelector.generating_pdf')}</h3>
        <div className="progress-info">
          <p>
            {t('pageSelector.rendering_page', { current, total })}
          </p>
          {statusMessage && (
            <p className="status-message">{statusMessage}</p>
          )}
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${(current / total) * 100}%` }}
            />
          </div>
        </div>
        <button onClick={onCancel} className="cancel-print-button">
          {t('pageSelector.cancel')}
        </button>
      </div>
    </div>,
    document.body,
  );
};

/**
 * PageSelectorModal - Modal component for selecting pages with thumbnails
 * Features:
 * - Lazy loading of thumbnails using Intersection Observer
 * - Search/filter functionality
 * - Keyboard navigation (Escape to close)
 * - Click outside to close
 * - Multi-page print selection
 */
const PageSelectorModal = ({
  isOpen,
  onClose,
  pages,
  // Page-list loading/error state so a failed fetch is never shown as a
  // project with no pages. pagesLoading/pagesError/onRetryPages are provided
  // by the parent (PdfControls) which owns the fetch.
  pagesLoading = false,
  pagesError = null,
  onRetryPages,
  currentPage,
  onPageSelect,
  pagesWithShapes = new Map(),
  displayKeys = [],
  displayUnitSystem = null,
  authUser,
  firebase,
  projectName,
  measurementSystem,
  selectedScaleKey,
  currentScaleProps,
  // New props for project/folder/version controls
  projects = [],
  allVersions = [],
  selectedVersions: parentSelectedVersions = [],
  availableFolders = [],
  selectedFolder: parentSelectedFolder = '',
  onFiltersApplied, // Callback when page is selected with new filters
  onProjectChange, // Callback for project change (immediate effect to load pages)
}) => {
  const { t } = useTranslation();
  const [searchFilter, setSearchFilter] = useState('');
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [visiblePages, setVisiblePages] = useState(new Set());
  const [pagesWithDrafts, setPagesWithDrafts] = useState(new Map());
  const [thumbnailSize, setThumbnailSize] = useState(300); // Default 140px
  const [showOnlyWithShapes, setShowOnlyWithShapes] = useState(false);
  const [selectedPageIndices, setSelectedPageIndices] = useState(
    new Set(),
  );
  const [isPrinting, setIsPrinting] = useState(false);
  const [printQuality, setPrintQuality] = useState('high');
  const [fontSizeScale, setFontSizeScale] = useState(1);
  const [exportAction, setExportAction] = useState('print');
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const exportMenuRef = useRef(null);

  useEffect(() => {
    if (!isExportMenuOpen) return;
    const handleClick = (e) => {
      if (
        exportMenuRef.current &&
        !exportMenuRef.current.contains(e.target)
      ) {
        setIsExportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () =>
      document.removeEventListener('mousedown', handleClick);
  }, [isExportMenuOpen]);

  // Local state for filters (independent from parent until page is selected)
  const [localSelectedFolder, setLocalSelectedFolder] = useState(
    parentSelectedFolder,
  );
  const [localSelectedVersions, setLocalSelectedVersions] = useState(
    parentSelectedVersions,
  );
  const [isVersionDropdownOpen, setIsVersionDropdownOpen] =
    useState(false);
  const [isFolderDropdownOpen, setIsFolderDropdownOpen] =
    useState(false);
  const [folderSearchFilter, setFolderSearchFilter] = useState('');
  const versionDropdownRef = useRef(null);
  const folderDropdownRef = useRef(null);
  const [printProgress, setPrintProgress] = useState({
    current: 0,
    total: 0,
  });
  const [printCancelled, setPrintCancelled] = useState(false);
  const printCancelledRef = useRef(false);
  const [statusMessage, setStatusMessage] = useState('');

  // Helper to check if a page has shapes
  /**
   * Calculate indentation level based on path depth
   */
  const getFolderIndentation = (path) => {
    if (!path) return 0;
    // Count segments - ignore leading /
    const segments = path.split('/').filter(Boolean);
    return (segments.length - 1) * 16; // 16px per level
  };

  /**
   * Get the display name of a folder (last segment)
   */
  const getFolderDisplayName = (path) => {
    if (!path) return t('pageSelector.all_folders');
    const segments = path.split('/').filter(Boolean);
    return segments[segments.length - 1];
  };

  const getEffectiveVersion = useCallback(
    (page, selectedVersions) => {
      if (!selectedVersions || selectedVersions.length === 0) {
        return page.version;
      }
      if (selectedVersions.includes(page.version)) {
        return page.version;
      }
      if (Array.isArray(page.alternativeVersions)) {
        for (const alt of page.alternativeVersions) {
          if (
            alt?.version &&
            selectedVersions.includes(alt.version)
          ) {
            return alt.version;
          }
        }
      }
      return page.version;
    },
    [],
  );

  const getPageIndexInVersion = useCallback(
    (targetPage, targetVersion) => {
      if (!pages || pages.length === 0) return 0;

      const versionPages = [];
      pages.forEach((p) => {
        if (
          localSelectedFolder &&
          p.folderPath !== localSelectedFolder
        ) {
          return;
        }

        if (p.version === targetVersion) {
          versionPages.push(p);
        } else if (Array.isArray(p.alternativeVersions)) {
          const alt = p.alternativeVersions.find(
            (a) => a.version === targetVersion,
          );
          if (alt) {
            versionPages.push(alt);
          }
        }
      });

      const index = versionPages.findIndex(
        (p) =>
          p.documentId === targetPage.documentId &&
          String(p.actualPageNumber) ===
            String(targetPage.actualPageNumber),
      );
      return index !== -1 ? index : 0;
    },
    [pages, localSelectedFolder],
  );

  const pageHasShapes = useCallback(
    (page) => {
      const pageVersions = new Set([page.version]);
      if (Array.isArray(page.alternativeVersions)) {
        page.alternativeVersions.forEach((alt) => {
          if (alt?.version) {
            pageVersions.add(alt.version);
          }
        });
      }

      const versionsToCheck =
        localSelectedVersions.length > 0
          ? localSelectedVersions
          : Array.from(pageVersions);

      return versionsToCheck.some((version) => {
        if (!pageVersions.has(version)) return false;

        let targetPage = page;
        if (
          version !== page.version &&
          Array.isArray(page.alternativeVersions)
        ) {
          const alt = page.alternativeVersions.find(
            (a) => a.version === version,
          );
          if (alt) targetPage = alt;
        }

        const pageIndexInVersion = getPageIndexInVersion(
          targetPage,
          version,
        );
        return (
          pagesWithShapes?.get?.(version)?.has(pageIndexInVersion) ||
          false
        );
      });
    },
    [pagesWithShapes, localSelectedVersions, getPageIndexInVersion],
  );

  const pageHasDraft = useCallback(
    (page) => {
      const pageVersions = new Set([page.version]);
      if (Array.isArray(page.alternativeVersions)) {
        page.alternativeVersions.forEach((alt) => {
          if (alt?.version) {
            pageVersions.add(alt.version);
          }
        });
      }

      const versionsToCheck =
        localSelectedVersions.length > 0
          ? localSelectedVersions
          : Array.from(pageVersions);

      return versionsToCheck.some((version) => {
        if (!pageVersions.has(version)) return false;

        let targetPage = page;
        if (
          version !== page.version &&
          Array.isArray(page.alternativeVersions)
        ) {
          const alt = page.alternativeVersions.find(
            (a) => a.version === version,
          );
          if (alt) targetPage = alt;
        }

        const pageIndexInVersion = getPageIndexInVersion(
          targetPage,
          version,
        );
        return (
          pagesWithDrafts?.get?.(version)?.has(pageIndexInVersion) ||
          false
        );
      });
    },
    [localSelectedVersions, pagesWithDrafts, getPageIndexInVersion],
  );

  const pageHasBadge = useCallback(
    (page) => pageHasShapes(page) || pageHasDraft(page),
    [pageHasDraft, pageHasShapes],
  );
  const modalRef = useRef(null);
  const gridRef = useRef(null);
  const observerRef = useRef(null);
  const thumbnailRefs = useRef({});
  const rootRef = useRef(null);

  // Filter pages based on search query, shapes filter, folder, and versions
  const filteredPages = useMemo(() => {
    if (!pages) return [];

    let result = pages;

    // Filter by folder
    if (localSelectedFolder) {
      result = result.filter(
        (page) => page.folderPath === localSelectedFolder,
      );
    }

    // Filter by selected versions
    // If no versions selected, show no pages
    // If some versions selected, show only those versions' pages
    // If all versions selected, show all pages
    if (allVersions.length > 0) {
      if (localSelectedVersions.length === 0) {
        // No versions selected - show no pages
        result = [];
      } else if (localSelectedVersions.length < allVersions.length) {
        // Some versions selected - keep any page with a selected version
        result = result.filter((page) => {
          const pageVersions = new Set([page.version]);
          if (Array.isArray(page.alternativeVersions)) {
            page.alternativeVersions.forEach((alt) => {
              if (alt && alt.version) {
                pageVersions.add(alt.version);
              }
            });
          }
          return localSelectedVersions.some((version) =>
            pageVersions.has(version),
          );
        });
      }
      // If all versions selected, don't filter (show all)
    }

    // Keep the filter aligned with the badge UI: green published pages and
    // red draft-only pages should both remain visible behind the same toggle.
    if (showOnlyWithShapes) {
      result = result.filter((page) => pageHasBadge(page));
    }

    // Filter by search query
    if (searchFilter.trim()) {
      const query = searchFilter.toLowerCase();
      result = result.filter(
        (page) =>
          page.pageLabel.toLowerCase().includes(query) ||
          (page.pageIndex + 1).toString().includes(query),
      );
    }

    return result;
  }, [
    pages,
    searchFilter,
    showOnlyWithShapes,
    pageHasBadge,
    localSelectedFolder,
    localSelectedVersions,
    allVersions,
  ]);

  // Reset visible pages when pages list changes (e.g., after project change)
  // This forces thumbnails to re-render and re-load
  useEffect(() => {
    setVisiblePages(new Set());
    setSelectedPageIndices(new Set());
  }, [pages]);

  useEffect(() => {
    if (
      !isOpen ||
      !authUser?.uid ||
      !firebase ||
      !projectName ||
      !pages ||
      pages.length === 0
    ) {
      setPagesWithDrafts(new Map());
      return;
    }

    let cancelled = false;

    const loadDraftPages = async () => {
      try {
        const organization = pages[0]?.organization;
        if (!organization) {
          setPagesWithDrafts(new Map());
          return;
        }

        const projectDrafts = await firebase.doGetUserPreference(
          authUser.uid,
          `replanDraftIndex/${organization}/${projectName}`,
        );

        if (cancelled || !projectDrafts) {
          if (!cancelled) {
            setPagesWithDrafts(new Map());
          }
          return;
        }

        const validPageKeys = new Set(
          pages.flatMap((page) => {
            if (typeof page?.pageIndex !== 'number') {
              return [];
            }

            const pageVersions = new Set();
            if (page.version) {
              pageVersions.add(page.version);
            }
            if (Array.isArray(page.alternativeVersions)) {
              page.alternativeVersions.forEach((alt) => {
                if (alt?.version) {
                  pageVersions.add(alt.version);
                }
              });
            }

            return Array.from(pageVersions).map(
              (version) => `${version}::${page.pageIndex}`,
            );
          }),
        );

        const draftEntries = Object.entries(projectDrafts)
          .flatMap(([encodedVersion, pageDrafts]) => {
            if (!pageDrafts || typeof pageDrafts !== 'object') {
              return [];
            }

            const version = decodePreferenceSegment(encodedVersion);
            return Object.entries(pageDrafts).map(
              ([pageIndex, draftData]) => ({
                version,
                pageIndex: Number.parseInt(pageIndex, 10),
                draftData,
              }),
            );
          })
          .filter(
            ({ version, pageIndex, draftData }) =>
              !Number.isNaN(pageIndex) &&
              draftData &&
              typeof draftData === 'object' &&
              validPageKeys.has(`${version}::${pageIndex}`),
          );

        const draftMap = new Map();
        draftEntries.forEach(({ version, pageIndex }) => {
          const existingPages = draftMap.get(version) || new Set();
          existingPages.add(pageIndex);
          draftMap.set(version, existingPages);
        });

        if (!cancelled) {
          setPagesWithDrafts(draftMap);
        }
      } catch (error) {
        console.error('Failed to load Replan draft pages:', error);
        if (!cancelled) {
          setPagesWithDrafts(new Map());
        }
      }
    };

    loadDraftPages();

    return () => {
      cancelled = true;
    };
  }, [authUser?.uid, firebase, isOpen, pages, projectName]);

  // Setup Intersection Observer for lazy loading
  useEffect(() => {
    if (!isOpen) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const pageIndex = parseInt(
            entry.target.dataset.pageIndex,
            10,
          );
          if (entry.isIntersecting) {
            setVisiblePages((prev) => new Set([...prev, pageIndex]));
          }
        });
      },
      {
        root: gridRef.current,
        rootMargin: '100px', // Pre-load thumbnails slightly before they enter viewport
        threshold: 0.1,
      },
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [isOpen]);

  // Observe thumbnail elements
  useEffect(() => {
    if (!observerRef.current || !isOpen) return;

    // Observe all current thumbnail refs
    Object.values(thumbnailRefs.current).forEach((ref) => {
      if (ref) {
        observerRef.current.observe(ref);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [filteredPages, isOpen]);

  // Handle keyboard events
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (isVersionDropdownOpen) {
          setIsVersionDropdownOpen(false);
        } else {
          onClose();
        }
      }
    };

    const handleClickOutside = (event) => {
      if (
        isVersionDropdownOpen &&
        versionDropdownRef.current &&
        !versionDropdownRef.current.contains(event.target)
      ) {
        setIsVersionDropdownOpen(false);
      }
      if (
        isFolderDropdownOpen &&
        folderDropdownRef.current &&
        !folderDropdownRef.current.contains(event.target)
      ) {
        setIsFolderDropdownOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, isVersionDropdownOpen, isFolderDropdownOpen]);

  // Handle click outside modal
  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose],
  );

  // Handle page selection - propagate filter changes only here
  const handlePageSelect = useCallback(
    (page) => {
      // Notify parent of filter changes when page is selected
      if (onFiltersApplied) {
        const excludedVersions = allVersions.filter(
          (v) => !localSelectedVersions.includes(v),
        );
        onFiltersApplied({
          folder: localSelectedFolder,
          excludedVersions: excludedVersions.join(','),
          navigateTo: {
            documentId: page.documentId,
            actualPageNumber: page.actualPageNumber,
          },
        });
      }
      onPageSelect(page.pageIndex + 1);
      onClose();
    },
    [
      onPageSelect,
      onClose,
      onFiltersApplied,
      localSelectedFolder,
      localSelectedVersions,
      allVersions,
    ],
  );

  // Scroll to current page when modal opens
  useEffect(() => {
    if (isOpen && gridRef.current && currentPage) {
      const currentThumbnail = thumbnailRefs.current[currentPage - 1];
      if (currentThumbnail) {
        setTimeout(() => {
          currentThumbnail.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }, 100);
      }
    }
  }, [isOpen, currentPage]);

  // Reset search when modal closes and sync local state when opening or when parent values change
  useEffect(() => {
    if (!isOpen) {
      setSearchFilter('');
      setVisiblePages(new Set());
      setSelectedPageIndices(new Set());
      setPrintCancelled(false);
      setIsVersionDropdownOpen(false);
      setIsSelectionMode(false);
    } else {
      // Sync local state with parent when modal opens or when parent values change
      // Default to current page's folder if no folder is explicitly selected in parent
      const currentPageFolder = pages?.find(
        (p) => p.pageIndex + 1 === currentPage,
      )?.folderPath;
      setLocalSelectedFolder(
        parentSelectedFolder || currentPageFolder || '',
      );
      setLocalSelectedVersions(parentSelectedVersions);
    }
  }, [
    isOpen,
    parentSelectedFolder,
    parentSelectedVersions,
    currentPage,
    pages,
    projectName,
  ]);

  // Handle checkbox change
  const handleCheckChange = useCallback((pageIndex, checked) => {
    setSelectedPageIndices((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(pageIndex);
      } else {
        newSet.delete(pageIndex);
      }
      return newSet;
    });
  }, []);

  // Select all/none toggle
  const handleSelectAll = useCallback(() => {
    if (selectedPageIndices.size === filteredPages.length) {
      setSelectedPageIndices(new Set());
    } else {
      setSelectedPageIndices(
        new Set(filteredPages.map((p) => p.pageIndex)),
      );
    }
  }, [filteredPages, selectedPageIndices.size]);

  // Load image from blob
  const loadImageFromBlob = useCallback((blob) => {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };
      img.src = url;
    });
  }, []);

  // Render a single page to dataURL
  const renderPageToDataURL = useCallback(
    async (page, selectedVersions, qualityPreset = 'high') => {
      if (printCancelled) throw new Error('Print cancelled');

      // Resolve which version to fetch
      const versionToFetch = getEffectiveVersion(
        page,
        selectedVersions,
      );

      // Resolve image identifiers: use alternativeVersions when target differs
      let imageDocumentId = page.documentId;
      let imagePageNumber = page.actualPageNumber;
      let targetPage = page;
      if (
        versionToFetch !== page.version &&
        Array.isArray(page.alternativeVersions)
      ) {
        const alt = page.alternativeVersions.find(
          (a) => a.version === versionToFetch,
        );
        if (alt) {
          imageDocumentId = alt.documentId;
          imagePageNumber = alt.actualPageNumber;
          targetPage = alt;
        }
      }

      // Fetch full-resolution image
      const imageBlob = await vcontrolAPI.getPageImage(
        imageDocumentId,
        imagePageNumber,
      );
      const konvaImage = await loadImageFromBlob(imageBlob);

      // Resolve pageIndex for shapes: find its index in the target version's page list
      const pageIndexToUse = getPageIndexInVersion(
        targetPage,
        versionToFetch,
      );

      // Fetch shapes for this page
      const organization = page.organization;
      if (!organization) {
        console.warn(
          'No organization found for page, skipping shapes',
        );
      }
      const doc = await firebase.doGetPageData(
        organization,
        projectName,
        versionToFetch,
        pageIndexToUse,
      );

      let shapes = [];
      let pageData = null;
      if (doc.exists) {
        pageData = doc.data();
        // Use getOrderedShapesFromPageData to handle both new (shapesById/shapeOrder) and legacy (shapes) formats
        shapes = getOrderedShapesFromPageData(pageData);
      }

      // Calculate scale factor using referenceWidth from DB
      const referenceWidth =
        pageData?.referenceWidth || konvaImage.width;
      const drawingScale = konvaImage.width / referenceWidth;
      const printDpi = (pageData?.dpi || 125) * drawingScale;

      // Keep original image dimensions - reduce size via JPEG compression only
      const imageSize = {
        width: konvaImage.width,
        height: konvaImage.height,
      };

      // Enhance shapes with measurements (using adjusted printDpi)
      const enhancedShapes = shapes.map((shape) =>
        recalculateShapeMeasurements(
          shape,
          currentScaleProps,
          printDpi,
          imageSize,
        ),
      );

      // Create a container for the off-screen stage (using portal within modal)
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-10000px';
      container.style.top = '-10000px';
      container.style.width = `${imageSize.width}px`;
      container.style.height = `${imageSize.height}px`;
      modalRef.current.appendChild(container);

      // Create a promise that resolves when the stage ref is set
      let stageRef = null;
      const stageRefPromise = new Promise((resolve) => {
        const setStageRef = (ref) => {
          stageRef = ref;
          if (ref) resolve(ref);
        };

        const root = createRoot(container);
        rootRef.current = root;
        root.render(
          <PrintStage
            ref={setStageRef}
            konvaImage={konvaImage}
            imageSize={imageSize}
            shapes={enhancedShapes}
            dpi={printDpi}
            drawingScale={drawingScale}
            currentScaleProps={currentScaleProps}
            displayKeys={displayKeys}
            displayUnitSystem={displayUnitSystem}
            fontSizeScale={fontSizeScale}
          />,
        );
      });

      // Wait for stage ref to be set
      await stageRefPromise;

      // Wait for React render cycle to complete
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Capture canvas as image after React renders completely
      const backgroundRect = stageRef.findOne('Rect');
      let originalFill = null;
      if (backgroundRect) {
        originalFill = backgroundRect.fill();
        backgroundRect.fill('white');
      }

      if (!stageRef) {
        throw new Error('Stage ref not set');
      }

      // Force all layers to draw
      const layers = stageRef.getLayers();
      layers.forEach((layer) => {
        layer.batchDraw();
      });

      // Wait for draws to complete
      await new Promise((resolve) =>
        requestAnimationFrame(() => {
          requestAnimationFrame(resolve);
        }),
      );

      if (printCancelled) {
        if (rootRef.current) {
          rootRef.current.unmount();
          rootRef.current = null;
        }
        container.remove();
        throw new Error('Print cancelled');
      }

      // Convert to dataURL for PDF export
      // Use JPEG compression to reduce file size while keeping original dimensions
      // Quality determined by selected print quality preset
      const q =
        PRINT_QUALITY_PRESETS[qualityPreset] ||
        PRINT_QUALITY_PRESETS.high;
      const dataUrl = stageRef.toDataURL({
        pixelRatio: q.pixelRatio,
        mimeType: 'image/jpeg',
        quality: q.jpegQuality,
      });

      // Restore and cleanup
      if (backgroundRect && originalFill)
        backgroundRect.fill(originalFill);
      if (rootRef.current) {
        rootRef.current.unmount();
        rootRef.current = null;
      }
      container.remove();

      return {
        dataUrl,
        width: imageSize.width,
        height: imageSize.height,
        label: page.pageLabel,
      };
    },
    [
      firebase,
      projectName,
      currentScaleProps,
      loadImageFromBlob,
      printCancelled,
      displayKeys,
      displayUnitSystem,
      getEffectiveVersion,
      getPageIndexInVersion,
      fontSizeScale,
    ],
  );

  // Generate PDF file directly (compatible with jsPDF 4.x)
  const generatePDF = useCallback(
    (pageDataArray) => {
      try {
        console.log(
          'Starting PDF generation with',
          pageDataArray.length,
          'pages',
        );

        // Determine if first page is landscape or portrait
        const firstPage = pageDataArray[0];
        console.log(
          'First page dimensions:',
          firstPage.width,
          'x',
          firstPage.height,
        );
        const isFirstLandscape = firstPage.width > firstPage.height;

        // For jsPDF 4.x, use simpler constructor - px unit with large dimensions causes issues
        // Convert px to mm at 96 DPI (common screen DPI)
        const pxToMm = (px) => (px * 25.4) / 96;

        const firstWidthMm = pxToMm(firstPage.width);
        const firstHeightMm = pxToMm(firstPage.height);

        console.log(
          'PDF dimensions in mm:',
          firstWidthMm,
          'x',
          firstHeightMm,
        );
        console.log(
          'Creating jsPDF with params:',
          isFirstLandscape ? 'l' : 'p',
          'mm',
          [firstWidthMm, firstHeightMm],
        );

        // Create PDF with orientation based on first page
        const pdf = new jsPDF(isFirstLandscape ? 'l' : 'p', 'mm', [
          firstWidthMm,
          firstHeightMm,
        ]);

        console.log('jsPDF instance created successfully');

        pageDataArray.forEach((pageData, index) => {
          const msgProcPg = t('pageSelector.processing_page', {
            current: index + 1,
            total: pageDataArray.length,
          });
          setStatusMessage(msgProcPg);
          console.log(msgProcPg);
          const widthMm = pxToMm(pageData.width);
          const heightMm = pxToMm(pageData.height);
          const isLandscape = pageData.width > pageData.height;

          if (index > 0) {
            const msgNewPg = t('pageSelector.adding_new_page', {
              width: widthMm.toFixed(2),
              height: heightMm.toFixed(2),
            });
            setStatusMessage(msgNewPg);
            console.log(
              'Adding new page:',
              widthMm,
              'x',
              heightMm,
              'mm',
            );
            // Add new page with same orientation and size as the image
            pdf.addPage([widthMm, heightMm], isLandscape ? 'l' : 'p');
          }

          const msgAddImg = t('pageSelector.adding_image_to_page', {
            current: index + 1,
          });
          setStatusMessage(msgAddImg);
          console.log('Adding image to page', index + 1);
          // Add image to fill the entire page (JPEG format for smaller file size)
          pdf.addImage(
            pageData.dataUrl,
            'JPEG',
            0,
            0,
            widthMm,
            heightMm,
          );
          setStatusMessage(
            t('pageSelector.image_added_successfully'),
          );
          console.log('Image added successfully');
        });

        console.log('PDF generation completed successfully');
        return pdf;
      } catch (error) {
        console.error('Error in generatePDF:', error);
        console.error('Error stack:', error.stack);
        console.error('Error name:', error.name);
        console.error('Error message:', error.message);
        throw new Error(
          t('pageSelector.pdf_generation_error', {
            error: error.message || error.toString(),
          }),
        );
      }
    },
    [setStatusMessage],
  );

  // Main print handler with chunking
  const handlePrintSelected = useCallback(async () => {
    const selectedPages = pages.filter((p) =>
      selectedPageIndices.has(p.pageIndex),
    );
    if (selectedPages.length === 0) return;

    setIsPrinting(true);
    setPrintCancelled(false);
    setPrintProgress({ current: 0, total: selectedPages.length });

    const CHUNK_SIZE = 2; // Reduced from 3-4 to 2 pages at a time for better stability
    const allPageData = [];

    try {
      for (let i = 0; i < selectedPages.length; i += CHUNK_SIZE) {
        if (printCancelled) break;

        const chunk = selectedPages.slice(i, i + CHUNK_SIZE);

        // Process chunk sequentially instead of parallel to avoid overwhelming the browser
        for (
          let chunkIndex = 0;
          chunkIndex < chunk.length;
          chunkIndex++
        ) {
          if (printCancelled) break;

          const page = chunk[chunkIndex];
          const pageNum = i + chunkIndex + 1;
          setPrintProgress({
            current: pageNum,
            total: selectedPages.length,
          });

          const pageData = await renderPageToDataURL(
            page,
            localSelectedVersions,
            printQuality,
          );
          allPageData.push(pageData);
        }
      }

      if (!printCancelled && allPageData.length > 0) {
        // Generate PDF and download
        setPrintProgress({
          current: allPageData.length,
          total: allPageData.length,
        });

        console.log(
          'About to generate PDF with',
          allPageData.length,
          'pages',
        );

        if (allPageData.length > 20) {
          const proceed = window.confirm(
            t('pageSelector.large_pdf_warning', {
              count: allPageData.length,
            }),
          );
          if (!proceed) {
            throw new Error('Print cancelled');
          }
        }

        const pdf = generatePDF(allPageData);
        console.log('PDF object created:', pdf);
        console.log('PDF save method type:', typeof pdf.save);

        // Create filename from project name and date
        const timestamp = new Date().toISOString().split('T')[0];
        const filename = `${projectName || 'Pages'}_${timestamp}.pdf`;

        console.log('Attempting to save PDF as:', filename);
        // Save the PDF using blob approach to avoid string length limits
        try {
          // Use blob output instead of save() to avoid building entire PDF as string
          const pdfBlob = pdf.output('blob');
          console.log(
            'PDF blob created, size:',
            pdfBlob.size,
            'bytes',
          );

          // Create download link and trigger download
          const blobUrl = URL.createObjectURL(pdfBlob);
          const downloadLink = document.createElement('a');
          downloadLink.href = blobUrl;
          downloadLink.download = filename;
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);

          // Clean up blob URL after a delay to ensure download starts
          setTimeout(() => URL.revokeObjectURL(blobUrl), 100);

          console.log('PDF save completed');
        } catch (saveError) {
          console.error('Error during pdf.save():', saveError);
          console.error('Save error stack:', saveError.stack);
          throw saveError;
        }
      }
    } catch (error) {
      if (error.message !== 'Print cancelled') {
        console.error('PDF generation failed:', error);
        alert(t('pageSelector.failed_to_generate_pdf'));
      }
    } finally {
      setIsPrinting(false);
      setPrintProgress({ current: 0, total: 0 });
    }
  }, [
    pages,
    selectedPageIndices,
    renderPageToDataURL,
    generatePDF,
    printCancelled,
    projectName,
    localSelectedVersions,
    printQuality,
  ]);

  const handleCancelPrint = useCallback(() => {
    setPrintCancelled(true);
  }, []);

  const handleExportAction = useCallback(
    (action) => {
      if (action === 'print') {
        handlePrintSelected();
      } else {
        alert(t('pageSelector.coming_soon'));
      }
    },
    [handlePrintSelected, t],
  );

  if (!isOpen) return null;

  return createPortal(
    <>
      {isPrinting && (
        <PrintProgressModal
          current={printProgress.current}
          total={printProgress.total}
          onCancel={handleCancelPrint}
          statusMessage={statusMessage}
        />
      )}
      <div
        className="page-selector-backdrop"
        onClick={handleBackdropClick}
      >
        <div className="page-selector-modal" ref={modalRef}>
          <div className="modal-header">
            {/* Project selector */}
            {projects.length > 0 && (
              <select
                value={projectName || ''}
                onChange={(e) => {
                  if (onProjectChange && e.target.value) {
                    onProjectChange(e.target.value);
                    // Reset all local filters when project changes (folders/versions are project-specific)
                    setLocalSelectedFolder('');
                    setLocalSelectedVersions([]);
                    setSearchFilter('');
                    setFolderSearchFilter('');
                  }
                }}
                className="modal-project-selector"
                title={t('pageSelector.select_project')}
              >
                <option value="">
                  {t('pageSelector.select_project')}
                </option>
                {projects.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            )}

            {/* Folder selector */}
            {availableFolders.length > 0 && (
              <div
                className="modal-folder-selector"
                ref={folderDropdownRef}
              >
                <button
                  type="button"
                  onClick={() => {
                    setIsFolderDropdownOpen(!isFolderDropdownOpen);
                    setFolderSearchFilter(''); // Reset search when opening
                  }}
                  className="modal-folder-dropdown-trigger"
                  title={t('pageSelector.filter_by_folder')}
                >
                  <span className="folder-icon">📂</span>
                  {getFolderDisplayName(localSelectedFolder)}
                </button>

                {isFolderDropdownOpen && (
                  <div className="modal-folder-dropdown">
                    <div className="folder-search-container">
                      <input
                        type="text"
                        className="folder-search-input"
                        placeholder={
                          t('pageSelector.search_folders') ||
                          'Search folders...'
                        }
                        value={folderSearchFilter}
                        onChange={(e) =>
                          setFolderSearchFilter(e.target.value)
                        }
                        autoFocus
                        onMouseDown={(e) => e.stopPropagation()} // Prevent closing when clicking input
                      />
                    </div>
                    <div className="folder-options">
                      <div
                        className={`folder-option ${!localSelectedFolder ? 'active' : ''}`}
                        onClick={() => {
                          setLocalSelectedFolder('');
                          setIsFolderDropdownOpen(false);
                        }}
                      >
                        <span className="folder-icon">📂</span>
                        {t('pageSelector.all_folders')}
                      </div>
                      {availableFolders
                        .filter(
                          (f) =>
                            !folderSearchFilter ||
                            f
                              .toLowerCase()
                              .includes(
                                folderSearchFilter.toLowerCase(),
                              ),
                        )
                        .map((folder) => (
                          <div
                            key={folder}
                            className={`folder-option ${localSelectedFolder === folder ? 'active' : ''}`}
                            style={{
                              paddingLeft: `${getFolderIndentation(folder) + 12}px`,
                            }}
                            onClick={() => {
                              setLocalSelectedFolder(folder);
                              setIsFolderDropdownOpen(false);
                            }}
                          >
                            <span className="folder-icon">📂</span>
                            {getFolderDisplayName(folder)}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Version selector */}
            {allVersions.length > 0 && (
              <div
                className="modal-version-selector"
                ref={versionDropdownRef}
              >
                <button
                  type="button"
                  onClick={() =>
                    setIsVersionDropdownOpen(!isVersionDropdownOpen)
                  }
                  className="modal-version-dropdown-trigger"
                  title={t('pageSelector.filter_by_versions')}
                >
                  {t('pageSelector.versions_count', {
                    selected: localSelectedVersions.length,
                    total: allVersions.length,
                  })}
                </button>

                {isVersionDropdownOpen && (
                  <div className="modal-version-dropdown">
                    <div className="version-actions">
                      <button
                        type="button"
                        className="tiny-button"
                        onClick={() =>
                          setLocalSelectedVersions([...allVersions])
                        }
                      >
                        {t('pageSelector.all')}
                      </button>
                      <button
                        type="button"
                        className="tiny-button"
                        onClick={() => setLocalSelectedVersions([])}
                      >
                        {t('pageSelector.none')}
                      </button>
                      <button
                        type="button"
                        className="tiny-button"
                        onClick={() =>
                          setIsVersionDropdownOpen(false)
                        }
                      >
                        ❌
                      </button>
                    </div>
                    <div className="version-options">
                      {allVersions.map((version) => (
                        <label
                          key={version}
                          className="version-option"
                        >
                          <input
                            type="checkbox"
                            checked={localSelectedVersions.includes(
                              version,
                            )}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setLocalSelectedVersions([
                                  ...localSelectedVersions,
                                  version,
                                ]);
                              } else {
                                setLocalSelectedVersions(
                                  localSelectedVersions.filter(
                                    (v) => v !== version,
                                  ),
                                );
                              }
                            }}
                          />
                          {version}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* <label
              htmlFor="thumbnail-size"
              className="slider-label"
            >
              {t('pageSelector.size')}
            </label> */}
            <input
              type="search"
              placeholder={t('pageSelector.filter_pages_placeholder')}
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="page-filter-input"
              autoFocus
            />
            <input
              id="thumbnail-size"
              type="range"
              step="50"
              min="200"
              max="400"
              value={thumbnailSize}
              onChange={(e) =>
                setThumbnailSize(Number(e.target.value))
              }
              className="thumbnail-size-slider"
              title={t('pageSelector.thumbnail_size_tooltip', {
                size: thumbnailSize,
              })}
            />

            <button
              className={`print-toggle-button ${isSelectionMode ? 'active' : ''}`}
              onClick={() => setIsSelectionMode(!isSelectionMode)}
              title={t('pageSelector.toggle_selection_mode')}
            >
              🖨️
            </button>
            <button
              className="close-button"
              onClick={onClose}
              title={t('pageSelector.close')}
            >
              ❌
            </button>
          </div>

          <div className="modal-body" ref={gridRef}>
            {pagesLoading ? (
              <div
                className="no-pages-message pages-loading"
                aria-busy="true"
              >
                {t('pageSelector.loading_pages')}
              </div>
            ) : pagesError ? (
              <div className="no-pages-message no-pages-error">
                <span>{t('pageSelector.no_pages_error')}</span>
                {onRetryPages && (
                  <button
                    className="thumbnail-retry-btn"
                    onClick={onRetryPages}
                    title={t('pageSelector.retry_loading_pages')}
                  >
                    {t('pageSelector.retry')}
                  </button>
                )}
              </div>
            ) : filteredPages.length === 0 ? (
              <div className="no-pages-message">
                {searchFilter
                  ? t('pageSelector.no_pages_match')
                  : t('pageSelector.no_pages_available')}
              </div>
            ) : (
              <div
                className="thumbnails-grid"
                style={{
                  gridTemplateColumns: `repeat(auto-fill, minmax(${thumbnailSize}px, 1fr))`,
                }}
              >
                {filteredPages.map((page) => (
                  <div
                    key={page.pageIndex}
                    ref={(el) =>
                      (thumbnailRefs.current[page.pageIndex] = el)
                    }
                    data-page-index={page.pageIndex}
                  >
                    <PageThumbnail
                      page={page}
                      isSelected={page.pageIndex + 1 === currentPage}
                      onSelect={handlePageSelect}
                      isVisible={visiblePages.has(page.pageIndex)}
                      hasShapes={pageHasShapes(page)}
                      hasDraft={pageHasDraft(page)}
                      isSelectionMode={isSelectionMode}
                      isChecked={selectedPageIndices.has(
                        page.pageIndex,
                      )}
                      onCheckChange={handleCheckChange}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <span className="page-count">
              {pagesLoading || pagesError
                ? ''
                : t('pageSelector.pages_count', {
                    filtered: filteredPages.length,
                    total: pages?.length || 0,
                  })}
            </span>
            <label
              className="shapes-only-toggle"
              title={t('pageSelector.show_only_drawings')}
            >
              <input
                type="checkbox"
                checked={showOnlyWithShapes}
                onChange={(e) =>
                  setShowOnlyWithShapes(e.target.checked)
                }
              />
              {t('pageSelector.only_drawings')}
            </label>
            {isSelectionMode && (
              <>
                <label
                  className="select-all-button"
                  title={
                    selectedPageIndices.size === filteredPages.length
                      ? t('pageSelector.deselect_all')
                      : t('pageSelector.select_all')
                  }
                >
                  <input
                    type="checkbox"
                    checked={
                      selectedPageIndices.size ===
                      filteredPages.length
                    }
                    onChange={handleSelectAll}
                  />
                  {t('pageSelector.select_all')}
                </label>
                <div
                  className="print-setting-group"
                  title={t('pageSelector.print_quality_tooltip')}
                >
                  <label className="print-setting-option quality-draft">
                    <input
                      type="radio"
                      name="printQuality"
                      value="draft"
                      checked={printQuality === 'draft'}
                      onChange={(e) =>
                        setPrintQuality(e.target.value)
                      }
                    />

                    <span className="option-label">
                      {t('pageSelector.quality_draft')}
                    </span>
                  </label>

                  <label className="print-setting-option quality-low">
                    <input
                      type="radio"
                      name="printQuality"
                      value="low"
                      checked={printQuality === 'low'}
                      onChange={(e) =>
                        setPrintQuality(e.target.value)
                      }
                    />
                    <span className="option-label">
                      {t('pageSelector.quality_low')}
                    </span>
                  </label>
                  <label className="print-setting-option quality-standard">
                    <input
                      type="radio"
                      name="printQuality"
                      value="standard"
                      checked={printQuality === 'standard'}
                      onChange={(e) =>
                        setPrintQuality(e.target.value)
                      }
                    />

                    <span className="option-label">
                      {t('pageSelector.quality_standard')}
                    </span>
                  </label>
                  <label className="print-setting-option quality-high">
                    <input
                      type="radio"
                      name="printQuality"
                      value="high"
                      checked={printQuality === 'high'}
                      onChange={(e) =>
                        setPrintQuality(e.target.value)
                      }
                    />
                    <span className="option-label">
                      {t('pageSelector.quality_high')}
                    </span>
                  </label>
                </div>

                <div
                  className="print-setting-group"
                  title={t('pageSelector.font_size_tooltip')}
                >
                  <label className="print-setting-option size-small">
                    <input
                      type="radio"
                      name="fontSizeScale"
                      value={0.8}
                      checked={fontSizeScale === 0.8}
                      onChange={(e) =>
                        setFontSizeScale(Number(e.target.value))
                      }
                    />
                    <span>{t('pageSelector.font_size_small')}</span>
                  </label>
                  <label className="print-setting-option size-normal">
                    <input
                      type="radio"
                      name="fontSizeScale"
                      value={1}
                      checked={fontSizeScale === 1}
                      onChange={(e) =>
                        setFontSizeScale(Number(e.target.value))
                      }
                    />
                    <span>{t('pageSelector.font_size_normal')}</span>
                  </label>
                  <label className="print-setting-option size-large">
                    <input
                      type="radio"
                      name="fontSizeScale"
                      value={2}
                      checked={fontSizeScale === 2}
                      onChange={(e) =>
                        setFontSizeScale(Number(e.target.value))
                      }
                    />
                    <span>{t('pageSelector.font_size_large')}</span>
                  </label>
                  <label className="print-setting-option size-xlarge">
                    <input
                      type="radio"
                      name="fontSizeScale"
                      value={3}
                      checked={fontSizeScale === 3}
                      onChange={(e) =>
                        setFontSizeScale(Number(e.target.value))
                      }
                    />
                    <span>{t('pageSelector.font_size_xlarge')}</span>
                  </label>
                </div>

                <div className="split-button" ref={exportMenuRef}>
                  <button
                    className="split-button-primary"
                    onClick={() => handleExportAction(exportAction)}
                    disabled={
                      isPrinting || selectedPageIndices.size === 0
                    }
                    title={t('pageSelector.print_selected_tooltip', {
                      count: selectedPageIndices.size,
                    })}
                  >
                    {exportAction === 'print' && (
                      <span> {t('pageSelector.export_print')}</span>
                    )}
                    {exportAction === 'email' && (
                      <span>{t('pageSelector.export_email')}</span>
                    )}
                    {exportAction === 'csv' && (
                      <span>{t('pageSelector.export_csv')}</span>
                    )}
                    
                    <span className="split-button-count">
                       ({selectedPageIndices.size})
                    </span>
                  </button>
                  <span className="vertical-separator" />
                  <button
                    className="split-button-trigger"
                    onClick={() =>
                      setIsExportMenuOpen((prev) => !prev)
                    }
                    disabled={
                      isPrinting || selectedPageIndices.size === 0
                    }
                    aria-label="Toggle export action menu"
                  >
                    ▼
                  </button>
                  {isExportMenuOpen && (
                    <div className="split-button-menu">
                      {exportAction !== 'print' && (
                        <button
                          onClick={() => {
                            setExportAction('print');
                            setIsExportMenuOpen(false);
                            handleExportAction('print');
                          }}
                        >
                          🖨️ {t('pageSelector.export_print')}
                        </button>
                      )}
                      {exportAction !== 'email' && (
                        <button
                          onClick={() => {
                            setExportAction('email');
                            setIsExportMenuOpen(false);
                            handleExportAction('email');
                          }}
                        >
                          ✉️ {t('pageSelector.export_email')}
                        </button>
                      )}
                      {exportAction !== 'csv' && (
                        <button
                          onClick={() => {
                            setExportAction('csv');
                            setIsExportMenuOpen(false);
                            handleExportAction('csv');
                          }}
                        >
                          📄 {t('pageSelector.export_csv')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default PageSelectorModal;
